"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { AssetImage } from "@/components/optilase/asset-image";
import { TextLink } from "@/components/optilase/text-link";
import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { cn } from "@/lib/utils";
import { usePublicPathname } from "@/lib/public-pathname";

type Asset = { filename?: string; alt?: string; focus?: string };

type HummFinanceBlok = SbBlokData & {
  title?: string;
  description?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  image?: Asset;
  badge_text?: string;
  bg_color?: unknown;
  reverse_order?: boolean;
};

export default function HummFinance({ blok }: { blok: HummFinanceBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const link = resolveLink(blok.button_link, region);
  const surface = surfaceFrom(blok.bg_color);
  const badgeText = blok.badge_text || "How much will it cost?";

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-10 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div className="mx-auto max-w-7xl">
        <div
          className={cn(
            "flex flex-col gap-12 lg:flex-row lg:items-center lg:gap-39",
            blok.reverse_order && "lg:flex-row-reverse",
          )}
        >
          <div className="flex flex-col gap-6 lg:w-125 lg:shrink-0 lg:gap-8">
            <div className="flex flex-col gap-4 lg:gap-5">
              {blok.title ? (
                <h2 className={cn("text-section", headingOn(surface, "text-teal-07"))}>
                  {blok.title}
                </h2>
              ) : null}
              {blok.description ? (
                <p
                  className={cn(
                    "text-base leading-relaxed tracking-[-0.01em]",
                    onSurface(surface, "text-white/85", "text-grey-07", "text-grey-07"),
                  )}
                >
                  {blok.description}
                </p>
              ) : null}
            </div>
            {blok.button_label && link.href !== "#" ? (
              <TextLink href={link.href} target={link.target} className="text-teal-07">
                {blok.button_label}
              </TextLink>
            ) : null}
          </div>

          {blok.image?.filename ? (
            <div className="relative order-first w-full lg:order-0 lg:w-123 lg:shrink-0">
              <div className="relative pt-3 pb-3 lg:py-0">
                <div className="relative ml-auto h-70.25 w-full overflow-hidden rounded-3xl lg:mr-[29px] lg:h-90 lg:w-102.75 lg:rounded-3xl">
                  <AssetImage
                    src={blok.image.filename}
                    focus={blok.image.focus}
                    alt={blok.image.alt || ""}
                    fill
                    sizes="(min-width: 1115px) 411px, 100vw"
                    className="object-cover"
                  />
                </div>

                {badgeText ? (
                  <p className="bg-teal-07 absolute top-0 right-[23px] flex w-29.75 items-center justify-center rounded-lg px-3 py-2.5 text-center text-sm leading-tight font-bold text-white lg:top-11.75 lg:right-0 lg:w-32.5 lg:px-4 lg:py-3 lg:text-base">
                    {badgeText}
                  </p>
                ) : null}

                <span className="absolute bottom-0 left-[13px] flex w-30.75 items-center justify-center rounded-lg bg-[#ff5a00] p-2 lg:top-60 lg:bottom-auto lg:left-0 lg:w-46.5">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/assets/logo/humm-logo-w.svg"
                    alt="humm"
                    width={1137}
                    height={242}
                    className="h-7.5 w-auto lg:h-11.25"
                  />
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
